import { cardMatchesPerson } from './personNames';

/**
 * Book Notes leftovers (#696 / #917). Bare /dossiers is the plot notebook.
 * Character leftover URLs still land on Book Notes — the named card, or the pile.
 * Plot leftover URLs stay on the plot notebook. Do not bounce plot to Book Notes.
 */

export const BOOK_CARDS_PATH = '/bibles';

/** Left-nav deep link into one Book Notes category tile. */
export function bookNotesCategoryPath(category) {
  const params = new URLSearchParams();
  params.set('category', category);
  return `${BOOK_CARDS_PATH}?${params.toString()}`;
}

export function bookNotesCategoryFromSearch(search = '') {
  const params = search instanceof URLSearchParams
    ? search
    : new URLSearchParams(String(search || '').replace(/^\?/, ''));
  return params.get('category') || '';
}

export const PLOT_NOTEBOOK_PATH = '/dossiers';

export const OPEN_WRITEUP = 'Open long write-up';

function asSearchParams(search) {
  if (search instanceof URLSearchParams) return search;
  const raw = search == null ? '' : String(search);
  return new URLSearchParams(raw.startsWith('?') ? raw.slice(1) : raw);
}

/**
 * Character leftover /dossiers?kind=characters → /bibles.
 * kind=plot stays on the plot notebook (/dossiers). This helper only
 * builds Book Notes paths, so a plot leftover returns the pile, not a
 * write-up bounce onto the Plot card.
 * Thin cards still land on the short version, not an empty second page.
 */
export function leftoverDossiersToCardsPath(search = '') {
  const params = asSearchParams(search);
  const next = new URLSearchParams();
  const kind = params.get('kind');
  const name = params.get('name');
  if (kind === 'characters' || name) {
    next.set('kind', 'characters');
    if (name) next.set('name', name);
    next.set('writeup', '1');
  }
  const qs = next.toString();
  return qs ? `${BOOK_CARDS_PATH}?${qs}` : BOOK_CARDS_PATH;
}

export function bookCardsPath(search = '') {
  const qs = String(search || '').replace(/^\?/, '');
  return qs ? `${BOOK_CARDS_PATH}?${qs}` : BOOK_CARDS_PATH;
}

export function characterWriteupHasContent(view) {
  return ((view && view.entries) || []).some((entry) => (
    entry && !entry.discarded && String(entry.content || '').trim()
  ));
}

export function plotWriteupHasContent(view) {
  if (String((view && view.rollup_text) || '').trim()) return true;
  const threads = (view && view.threads) || [];
  return threads.some((thread) => (
    (thread.entries || []).some((entry) => (
      entry && !entry.discarded && String(entry.content || '').trim()
    ))
  ));
}

export function normCardName(value) {
  return (value || '').trim().toLowerCase();
}

export function leftoverKindKey(kind) {
  return String(kind || '').trim().toLowerCase();
}

/** Theme / Logline Tasks leftover — not the generic Book Notes pile. */
export function isThemeOrLoglineKind(kind) {
  const key = leftoverKindKey(kind);
  return key === 'theme' || key === 'plot' || key === 'logline';
}

/** Map a leftover kind onto the Book Notes category string. */
export function leftoverKindToCategory(kind) {
  const key = leftoverKindKey(kind);
  if (key === 'theme') return 'Theme';
  if (key === 'plot' || key === 'logline') return 'Plot';
  if (key === 'characters' || key === 'character') return 'Character';
  return '';
}

export function findBookCard(entries, { kind, name } = {}) {
  const rows = entries || [];
  const key = leftoverKindKey(kind);
  if (key === 'theme') {
    const matches = rows.filter((entry) => (entry.category || '').trim().toLowerCase() === 'theme');
    if (matches.length <= 1) return matches[0] || null;
    return [...matches].sort((a, b) => {
      const ta = Date.parse(a.updated_at || a.created_at || 0) || 0;
      const tb = Date.parse(b.updated_at || b.created_at || 0) || 0;
      return tb - ta;
    })[0];
  }
  if (key === 'plot' || key === 'logline') {
    return rows.find((entry) => (entry.category || '').trim().toLowerCase() === 'plot') || null;
  }
  const wanted = normCardName(name);
  if (!wanted) return null;
  const pool = kind === 'characters'
    ? rows.filter((entry) => (entry.category || '').toLowerCase() === 'character')
    : rows;
  return pool.find((entry) => (
    normCardName(entry.instance_name) === wanted
    || normCardName(entry.name) === wanted
    || cardMatchesPerson(entry, name)
  )) || null;
}
